import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  DocumentNode,
  LazyQueryExecFunction,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useLazyQuery
} from '@apollo/client';
import { setButtonLoading, setError, setLoading } from '@common/slices';
import { extractMessage } from '@utils/helper-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';

export function useApiLazyQuery<TData = any, TVariables = OperationVariables>(
  query: DocumentNode,
  key: string,
  options?: QueryHookOptions<TData, TVariables>,
  config?: { successMsg?: string; withLoader?: boolean; buttonLoader?: boolean }
): [LazyQueryExecFunction<TData, TVariables>, QueryResult<TData, TVariables>] {
  const dispatch = useDispatch();
  const withLoader = config?.withLoader ?? false; // default true
  const buttonLoader = config?.buttonLoader ?? false; // default false

  const abortControllerRef = useRef<AbortController | null>(null);
  const location = useLocation();

  const context = useRef({
    fetchOptions: {
      signal: undefined as AbortSignal | undefined
    }
  }).current;

  // Set the signal if the request is active
  if (!context.fetchOptions.signal) {
    abortControllerRef.current = new AbortController();
    context.fetchOptions.signal = abortControllerRef.current.signal;
  }

  const [fetch, result] = useLazyQuery<TData, TVariables>(query, {
    ...options,
    context,
    onCompleted: (data: TData) => {
      if (withLoader) {
        dispatch(setLoading({ key, value: false }));
      }
      if (buttonLoader) {
        dispatch(setButtonLoading('success'));
        setTimeout(() => dispatch(setButtonLoading('idle')), 500);
      }
      if (config?.successMsg) {
        toast.success(
          <ToastSuccessMessage>{config.successMsg}</ToastSuccessMessage>
        );
      }
      if (options?.onCompleted) options.onCompleted(data);
    },
    onError: (error: any) => {
      if (withLoader) {
        dispatch(setLoading({ key, value: false }));
      }
      if (buttonLoader) {
        dispatch(setButtonLoading('idle'));
      }
      if (!error.message.includes('aborted')) {
        const cleanMessage = extractMessage(error.message);
        if (options?.onError) options.onError(error);
        dispatch(setError({ key, error: cleanMessage }));
        toast.error(<ToastErrorMessage>{cleanMessage}</ToastErrorMessage>);
      }
    }
  });

  const wrappedFetch = (...args: any) => {
    if (withLoader) {
      dispatch(setLoading({ key, value: true }));
    }
    if (buttonLoader) {
      dispatch(setButtonLoading('success'));
    }
    dispatch(setError({ key, error: null }));

    return fetch(...args);
  };

  // Cancel request on unmount only
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []); // Empty dependency array - only run on mount/unmount

  useEffect(() => {
    if (result.loading) {
      if (withLoader) {
        dispatch(setLoading({ key, value: true }));
      }
      if (buttonLoader) {
        dispatch(setButtonLoading('loading'));
      }
      dispatch(setError({ key, error: null }));
    }
  }, [result.loading]);

  return [wrappedFetch, result];
}
